import React, { useState, useEffect, useMemo } from 'react';
import './DeleteAccount.scss';
import {
  deleteAccount,
  getCurrentUserId,
  signOut,
  checkAuth,
  getAccessToken,
} from '../../../awsAuth';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API ||
  (typeof window !== 'undefined' &&
  window.location.hostname.includes('onrender.com')
    ? 'https://cvanto-api.onrender.com'
    : 'http://localhost:5000');

const API = API_BASE;

const INACTIVITY_OPTIONS = [1, 3, 6, 12, 18, 24];

const DeleteAccount = () => {
  const [confirmationText, setConfirmationText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { setIsLoggedIn } = useAuth();

  // auto-delete
  const userId = useMemo(() => getCurrentUserId(), []);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoMonths, setAutoMonths] = useState(6);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoError, setAutoError] = useState('');
  const [autoSuccess, setAutoSuccess] = useState('');
  const [lastActiveAt, setLastActiveAt] = useState(null);

  // load setting
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId) return;
      setAutoLoading(true);
      setAutoError('');
      try {
        const r = await fetch(
          `${API}/api/account/${encodeURIComponent(userId)}/auto-delete`
        );
        if (!r.ok)
          throw new Error('Не вдалося отримати налаштування автовидалення');
        const data = await r.json();
        if (cancelled) return;
        setAutoEnabled(!!data.enabled);
        setAutoMonths(Number(data.months || 6));
        setLastActiveAt(data.lastActiveAt || null);
      } catch (e) {
        if (!cancelled)
          setAutoError(
            e?.message || 'Помилка завантаження налаштування'
          );
      } finally {
        if (!cancelled) setAutoLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // toggle save
  const handleToggleAuto = async (checked) => {
    if (!userId) return;
    setAutoEnabled(checked);
    setAutoLoading(true);
    setAutoError('');
    setAutoSuccess('');
    try {
      const token = await getAccessToken?.().catch(() => null);
      const r = await fetch(
        `${API}/api/account/${encodeURIComponent(userId)}/auto-delete`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            enabled: !!checked,
            months: Number(autoMonths),
          }),
        }
      );
      if (!r.ok) throw new Error('Не вдалося зберегти налаштування');
      const data = await r.json();
      setLastActiveAt(data.lastActiveAt || lastActiveAt);
      setAutoSuccess(
        checked ? 'Автовидалення увімкнено' : 'Автовидалення вимкнено'
      );
    } catch (e) {
      setAutoEnabled((prev) => !prev);
      setAutoError(e?.message || 'Помилка збереження');
    } finally {
      setAutoLoading(false);
    }
  };

  // save period
  const handleSaveAuto = async () => {
    if (!userId) return;
    setAutoLoading(true);
    setAutoError('');
    setAutoSuccess('');
    try {
      const token = await getAccessToken?.().catch(() => null);
      const r = await fetch(
        `${API}/api/account/${encodeURIComponent(userId)}/auto-delete`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            enabled: !!autoEnabled,
            months: Number(autoMonths),
          }),
        }
      );
      if (!r.ok) throw new Error('Не вдалося зберегти налаштування');
      const data = await r.json();
      setLastActiveAt(data.lastActiveAt || lastActiveAt);
      setAutoSuccess('Збережено');
    } catch (e) {
      setAutoError(e?.message || 'Помилка збереження');
    } finally {
      setAutoLoading(false);
    }
  };

  // delete flow
  const handleDelete = async () => {
    setIsDeleting(true);
    setError('');

    const uid = getCurrentUserId();
    if (!uid) {
      setError('Не вдалося визначити користувача');
      setIsDeleting(false);
      return;
    }

    try {
      await checkAuth();
      const token = await getAccessToken?.().catch(() => null);
      await deleteAccount();

      const response = await fetch(`${API}/api/account/${uid}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok)
        throw new Error('Не вдалося видалити акаунт з бази');

      signOut(setIsLoggedIn);
      setSuccess('Акаунт успішно видалено');
      setTimeout(() => navigate('/auth'), 500);
    } catch (err) {
      console.error('Повна помилка при видаленні акаунта:', err);
      setError('Не вдалося завершити видалення акаунта');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="delete-account">
      {/* */}
      <section className="auto-minimal">
        <div className="auto-row">
          <div className="auto-title">
            <h3>Автовидалення при неактивності</h3>
            <p className="subtle">
              Якщо не заходити певний час — акаунт буде видалено.
            </p>
          </div>

          <label className="switch-min">
            <input
              type="checkbox"
              checked={autoEnabled}
              onChange={(e) => handleToggleAuto(e.target.checked)}
              disabled={autoLoading}
            />
            <span className="slider" />
          </label>
        </div>

        {autoEnabled && (
          <div className="auto-controls">
            <label className="inline-label">
              Через:
              <select
                value={autoMonths}
                onChange={(e) =>
                  setAutoMonths(Number(e.target.value))
                }
                disabled={autoLoading}
              >
                {INACTIVITY_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m} міс.
                  </option>
                ))}
              </select>
            </label>

            <button
              className="btn-save"
              onClick={handleSaveAuto}
              disabled={autoLoading}
            >
              {autoLoading ? 'Зберігаю…' : 'Зберегти'}
            </button>
          </div>
        )}

        <div className="auto-foot">
          <span className="help">
            {autoLoading
              ? 'Завантаження…'
              : lastActiveAt
              ? `Остання активність: ${new Date(
                  lastActiveAt
                ).toLocaleString()}`
              : 'Остання активність: —'}
          </span>

          {(autoError || autoSuccess) && (
            <span className={autoError ? 'msg err' : 'msg ok'}>
              {autoError || autoSuccess}
            </span>
          )}
        </div>
      </section>

      {/* */}
      <h2>Видалення акаунта</h2>
      <p>
        Цю дію не можна буде скасувати. Усі ваші дані буде
        безповоротно видалено.
      </p>
      <p>
        <strong>Щоб підтвердити, введіть:</strong>{' '}
        <code>permanently delete</code>
      </p>

      <input
        type="text"
        placeholder="Введіть: permanently delete"
        value={confirmationText}
        onChange={(e) => setConfirmationText(e.target.value)}
        className="confirmation-input"
      />

      <button
        className="delete-button"
        onClick={handleDelete}
        disabled={
          confirmationText !== 'permanently delete' || isDeleting
        }
      >
        {isDeleting ? 'Видаляю...' : 'Видалити акаунт'}
      </button>

      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}
    </div>
  );
};

export default DeleteAccount;
